/**
 * Dashboard service for fetching statistics
 */

import { supabase } from './supabase';

/**
 * Dashboard service object with statistical queries
 */
export const dashboardService = {
  /**
   * Fetches dashboard summary statistics
   * @returns {Promise<Object>} Dashboard stats
   */
  async getDashboardStats() {
    const { data, error } = await supabase.rpc('get_dashboard_stats');

    if (error) throw error;
    return data?.[0] || {
      total_bottles: 0,
      total_distilleries: 0,
      total_value: 0,
      estimated_value: 0,
      unopened_count: 0,
      opened_count: 0,
      empty_count: 0
    };
  },

  /**
   * Fetches bottle counts by type
   * @returns {Promise<Array>} Type statistics
   */
  async getBottlesByType() {
    const { data, error } = await supabase.rpc('get_bottles_by_type');

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches bottle counts by status
   * @returns {Promise<Array>} Status statistics
   */
  async getBottlesByStatus() {
    const { data, error } = await supabase.rpc('get_bottles_by_status');

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches bottle counts by country
   * @returns {Promise<Array>} Country statistics
   */
  async getBottlesByCountry() {
    const { data, error } = await supabase.rpc('get_bottles_by_country');

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches bottle counts by distillery state/region
   * @returns {Promise<Array>} State statistics
   */
  async getBottlesByState() {
    const { data, error } = await supabase.rpc('get_bottles_by_state');

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches bottle counts bucketed by purchase price range.
   * Bottles without a recorded price are excluded.
   * @returns {Promise<Array>} Price range statistics
   */
  async getBottlesByPriceRange() {
    const { data, error } = await supabase.rpc('get_bottles_by_price_range');

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches distillery counts by country
   * @returns {Promise<Array>} Country statistics
   */
  async getDistilleriesByCountry() {
    const { data, error } = await supabase.rpc('get_distilleries_by_country');

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches recent bottles added
   * @param {number} limit - Number of recent bottles to fetch
   * @returns {Promise<Array>} Recent bottles
   */
  async getRecentBottles(limit = 5) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('bottles')
      .select('id, bottle_name, type, status, purchase_date, distilleries(distillery_name)')
      .eq('user_id', user.id)
      .neq('status', 'Ordered')
      .order('purchase_date', { ascending: false, nullsFirst: false })
      .order('bottle_name', { ascending: true })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches recently emptied bottles
   * @param {number} limit - Number of bottles to fetch
   * @returns {Promise<Array>} Recently emptied bottles
   */
  async getRecentlyEmptiedBottles(limit = 5) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('bottles')
      .select('id, bottle_name, type, status, updated_at, distilleries(distillery_name)')
      .eq('user_id', user.id)
      .eq('status', 'Empty')
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches ordered bottles sorted by date added descending
   * @param {number} limit - Number of bottles to fetch
   * @returns {Promise<Array>} Ordered bottles
   */
  async getOrderedBottles() {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('bottles')
      .select('id, bottle_name, type, status, purchase_date, distilleries(distillery_name)')
      .eq('user_id', user.id)
      .eq('status', 'Ordered')
      .order('purchase_date', { ascending: false, nullsFirst: false })
      .order('bottle_name', { ascending: true });

    if (error) throw error;
    return data || [];
  }
};
